import { useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import StepShell from '@/components/StepShell';
import OptionCard from '@/components/OptionCard';
import Ornament from '@/components/Ornament';
import PhSelector from '@/components/PhSelector';
import TempSlider from '@/components/TempSlider';
import { useSession } from '@/store/session';
import type { SkinTexture, SkinType } from '@/types';

const SKIN_TYPES: Array<{ id: SkinType; label: string; desc: string; icon: string }> = [
  { id: 'seca',   label: 'Seca',   desc: 'Mate · necesita fijadores potentes', icon: '◌' },
  { id: 'normal', label: 'Normal', desc: 'Equilibrada',                         icon: '◐' },
  { id: 'grasa',  label: 'Grasa',  desc: 'Brillo · amplifica notas dulces',     icon: '●' },
];

const TEXTURES: Array<{ id: SkinTexture; label: string; desc: string }> = [
  { id: 'lisa',   label: 'Lisa',   desc: 'Poros finos · notas frescas y volátiles' },
  { id: 'media',  label: 'Media',  desc: 'Equilibrada' },
  { id: 'rugosa', label: 'Rugosa', desc: 'Poros marcados · soporta perfumes densos' },
];

export default function SkinMeasures() {
  const navigate = useNavigate();
  const { skin, setSkin } = useSession();

  const tempC = skin.tempC ?? 33.0;

  const isValid = useMemo(() => {
    return Boolean(skin.ph && skin.tempC != null && skin.type && skin.texture);
  }, [skin]);

  return (
    <StepShell
      step={3}
      title="Mediciones de tu piel"
      subtitle="El vendedor toma 4 medidas rápidas. Cambian cómo se proyecta cada perfume en ti."
      onBack={() => navigate('/cliente')}
      onNext={() => navigate('/preferencias')}
      nextDisabled={!isValid}
    >
      {/* 3.1 — pH */}
      <section>
        <header className="mb-3">
          <h3 className="label-section">
            1 · pH de la piel
          </h3>
          <p className="text-xs text-elixir-cream/55 mt-1">
            Apoya la tira reactiva en el antebrazo humedecido. Selecciona el color al que más se parece.
          </p>
        </header>
        <PhSelector value={skin.ph} onChange={(ph) => setSkin({ ph })} />
      </section>

      <Ornament />

      {/* 3.2 — Temperatura */}
      <section>
        <header className="mb-3">
          <h3 className="label-section">
            2 · Temperatura de la piel
          </h3>
          <p className="text-xs text-elixir-cream/55 mt-1">
            Apunta el termómetro infrarrojo al antebrazo. Determina la velocidad de evaporación.
          </p>
        </header>
        <TempSlider value={tempC} onChange={(t) => setSkin({ tempC: Math.round(t * 10) / 10 })} />
      </section>

      <Ornament />

      {/* 3.3 — Tipo de piel */}
      <section>
        <header className="mb-3">
          <h3 className="label-section">
            3 · Tipo de piel
          </h3>
          <p className="text-xs text-elixir-cream/55 mt-1">Evaluación visual y al tacto.</p>
        </header>
        <div className="grid grid-cols-3 gap-3">
          {SKIN_TYPES.map((t) => (
            <OptionCard
              key={t.id}
              size="lg"
              label={t.label}
              description={t.desc}
              icon={<span className="text-3xl text-elixir-gold">{t.icon}</span>}
              active={skin.type === t.id}
              onClick={() => setSkin({ type: t.id })}
            />
          ))}
        </div>
      </section>

      <Ornament />

      {/* 3.4 — Textura */}
      <section>
        <header className="mb-3">
          <h3 className="label-section">
            4 · Textura de la piel
          </h3>
          <p className="text-xs text-elixir-cream/55 mt-1">
            Adherencia: cuánto agarra el perfume a la piel.
          </p>
        </header>
        <div className="grid grid-cols-3 gap-3">
          {TEXTURES.map((t) => (
            <OptionCard
              key={t.id}
              size="lg"
              label={t.label}
              description={t.desc}
              active={skin.texture === t.id}
              onClick={() => setSkin({ texture: t.id })}
            />
          ))}
        </div>
      </section>

      {!isValid && (
        <p className="mt-6 text-xs text-elixir-cream/45 text-center">
          Completa las 4 mediciones para continuar.
        </p>
      )}
    </StepShell>
  );
}
